/**
 * PageFooterElement — F12.H T12.H.2.
 *
 * Stopka strony rekordu — kontener na dole formularza (np. „Data utworzenia",
 * linki dodatkowe, copyright). Może mieć dzieci (children).
 *
 * Settings: { text?, showSeparator?: boolean }
 */

import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/Checkbox';
import type {
  LayoutElementDefinition,
  LayoutElementPropertiesEditorProps,
} from '@/components/fields/LayoutElementRegistry';
import { Textarea } from '@/components/Textarea';

function PageFooterProperties({ settings, onChange }: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const text = String(settings.text ?? '');
  const showSeparator = settings.showSeparator !== false;
  return (
    <div className="space-y-3" data-testid="page-footer-properties-editor">
      <Textarea
        label={t('tenant.systemModuleBuilder.pageFooter.textLabel')}
        value={text}
        rows={2}
        onChange={(e) => onChange({ text: e.target.value })}
        data-testid="page-footer-prop-text"
      />
      <Checkbox
        label={t('tenant.systemModuleBuilder.pageFooter.showSeparator')}
        checked={showSeparator}
        onChange={(e) => onChange({ showSeparator: e.target.checked })}
        data-testid="page-footer-prop-separator"
      />
    </div>
  );
}

export const PageFooterElement: LayoutElementDefinition = {
  type: 'page-footer',
  category: 'struktura',
  isContainer: true,
  label: 'Stopka',
  icon: 'PanelBottom',
  defaultSettings: () => ({
    text: '',
    showSeparator: true,
  }),
  propertiesEditor: (props) => <PageFooterProperties {...props} />,
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      text?: string;
      showSeparator?: boolean;
    };
    const isBuilder = mode === 'builder';
    const hasChildren = children.length > 0;

    return (
      <footer
        className={`${settings.showSeparator !== false ? 'border-t border-border-primary pt-3' : ''} mt-4 ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition rounded p-2' : ''}`}
        data-testid={`page-footer-element-${element.id}`}
        data-element-type="page-footer"
      >
        {settings.text && <p className="text-xs text-text-tertiary">{settings.text}</p>}
        {hasChildren && <div className="mt-2 space-y-2">{children}</div>}
        {isBuilder && !hasChildren && !settings.text && (
          <p className="text-xs italic text-text-muted">
            (stopka pusta — przeciągnij elementy lub ustaw tekst)
          </p>
        )}
      </footer>
    );
  },
};
